import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Contact, ContactsFilters, CreateContactPayload, UpdateContactPayload } from '@/types/contacts';

interface ContactsResponse {
  contacts: Contact[];
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useContacts(filters: ContactsFilters = {}) {
  return useQuery({
    queryKey: ['contacts', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.createdBy) params.set('createdBy', filters.createdBy);
      const qs = params.toString();
      const { data } = await api.get<ContactsResponse>(`/api/contacts${qs ? `?${qs}` : ''}`);
      return data.contacts ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useContact(contactId: string | null) {
  return useQuery({
    queryKey: ['contacts', 'detail', contactId],
    queryFn: async () => {
      const { data } = await api.get<{ contact: Contact } | Contact>(`/api/contacts/${contactId}`);
      return ((data as any)?.contact || data) as Contact;
    },
    enabled: !!contactId,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useContactMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['contacts'] });

  const createContact = useMutation({
    mutationFn: async (payload: CreateContactPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/contacts', payload, { skipErrorToast: true });
      return data;
    },
    onSuccess: invalidate,
  });

  const updateContact = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateContactPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/contacts/${id}`, payload, { skipErrorToast: true });
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteContact = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/contacts/${id}`, { skipErrorToast: true });
      return data;
    },
    onSuccess: invalidate,
  });

  return { createContact, updateContact, deleteContact };
}
